import { ComponentBase } from '@abstracts/component-base';
import { Component, Inject, Injector, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CustomerDto } from '@dto/customerDto';
import { MasterUserOfCustomer } from '@dto/masterUserOfCustomer';
import { UserDto } from '@dto/userDto';
import { CustomersService } from '@services/entity/customers.service';
import { UsersService } from '@services/entity/users.service';
import { FormService } from '@services/form/form.service';
import Swal from 'sweetalert2';
import { CustomerNoteDto } from '../../../../dto/customerNoteDto';
import { PatchCustomerNote } from '../../../../dto/httpPatch/patchCustomerNote';
import { CustomerNote } from '../../../../models/customerNote';

@Component({
  selector: 'app-customer-history',
  templateUrl: './customer-history.component.html',
  styleUrls: ['./customer-history.component.scss']
})
export class CustomerHistoryComponent extends ComponentBase implements OnInit {

  public className = 'CustomerHistoryComponent';

  private customerId: number;

  public customer: CustomerDto;
  public customerNotes: CustomerNoteDto[];
  public customerNotesFiltered: CustomerNoteDto[];
  public masterUser: MasterUserOfCustomer;

  public formCreated: boolean;

  private editingEnabledNote: CustomerNoteDto;
  public editForm: UntypedFormGroup;

  public searchTerm: string;

  constructor(
    private customersService: CustomersService,
    private usersService: UsersService,
    public formService: FormService,
    public activatedRoute: ActivatedRoute,
    injector: Injector) {
      super(injector);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.componentSettings.footerVisible = false;
    this.customerId = this.activatedRoute.parent.snapshot.params.key;
    this.loadData();
  }

  loadData() {
    this.loadCustomer();
    this.loadNotes();
    this.loadMasterUser();
  }

  loadCustomer() {
    this.customersService.getCustomer(this.customerId).subscribe(result => {
      this.customer = result;
      this.checkIfAllDataLoaded();
    })
  }

  loadNotes() {
    this.customersService.getCustomerNotes(this.customerId).subscribe(result => {
      this.customerNotes = result;
      this.customerNotesFiltered = result;
      this.customerNotesFiltered = this.customerNotesFiltered.reverse();
      this.search();
      this.checkIfAllDataLoaded();
    })
  }

  loadMasterUser() {
    this.customersService.getMasterUserOfCustomer(this.customerId).subscribe(result => {
      this.masterUser = result;
      this.masterUser["genderDisplay"] = this.mapGender();
      this.checkIfAllDataLoaded();
    })
  }

  checkIfAllDataLoaded() {
    if (this.customerNotesFiltered && this.masterUser) {
      this.createForm();
    }
  }

  createForm() {
    this.formService.validators = {
      subject: [Validators.required],
      content: [Validators.required],
      author: [Validators.required]
    }

    this.formService.form = new UntypedFormGroup({
      subject: new UntypedFormControl('', this.formService.validators['subject']),
      content: new UntypedFormControl('', this.formService.validators['content']),
      author: new UntypedFormControl('', this.formService.validators['author'])
    });

    this.editForm = new UntypedFormGroup({
      subject: new UntypedFormControl('', Validators.required),
      content: new UntypedFormControl('', Validators.required)
    });

    this.formCreated = true;
  }

  saveNote() {
    var formValue = this.formService.form.value;

    const customerNote = new CustomerNote(formValue);

    this.customersService.createCustomerNote(this.customerId, customerNote).subscribe(result => {
      this.clearInput();
      this.loadNotes();
    })
  }

  onEditSubmit(formValue) {
    const patchCustomerNote: PatchCustomerNote = {
      subject: formValue.subject,
      content: formValue.content
    }

    this.customersService.patchCustomerNote(this.customerId, this.editingEnabledNote.id, patchCustomerNote).subscribe(result => {
      this.editingEnabledNote = null;
      this.loadNotes();
    }, error => { });
  }

  deleteNote(note: CustomerNoteDto) {
    this.customersService.deleteCustomerNote(note.id).subscribe(result => {
      this.loadNotes();
    })
  }

  editNote(note: CustomerNoteDto) {
    if (this.isEditingEnabled(note)) {
      this.editingEnabledNote = null;
      this.editForm.get('subject').setValue('');
      this.editForm.get('content').setValue('');
    }
    else {
      this.editingEnabledNote = note;
      this.editForm.get('subject').setValue(note.subject);
      this.editForm.get('content').setValue(note.content);
    }
  }

  isEditingEnabled(note: CustomerNoteDto) {
    return this.editingEnabledNote?.id == note.id;
  }

  cancelEdit() {
    this.editingEnabledNote = null;
    this.editForm.get('subject').setValue('');
    this.editForm.get('content').setValue('');
  }

  showConfirmDeleteDialog(note: CustomerNoteDto) {
    Swal.fire({
      title: 'Kundennotiz löschen?',
      text: 'Wollen Sie die Kundennotiz wirklich löschen?',
      icon: 'question',
      confirmButtonText: 'Bestätigen',
      confirmButtonColor: '#34383a',
      showCancelButton: true,
      cancelButtonText: 'Abbrechen',
      cancelButtonColor: 'red'
    }).then((result) => {
      if (result.isConfirmed) {
        this.deleteNote(note);
      }
    })
  }

  search() {
    if (!this.searchTerm || this.searchTerm == '') {
      this.customerNotesFiltered = this.customerNotes;
      return;
    }

    this.customerNotesFiltered = this.customerNotes.filter(x =>
      x.subject.toLowerCase().includes(this.searchTerm?.toLowerCase()) ||
      x.content.toLowerCase().includes(this.searchTerm?.toLowerCase()) ||
      x.author.toLowerCase().includes(this.searchTerm?.toLowerCase())
    );
  }

  clearInput() {
    this.formService.form.reset();
  }

  mapGender() {
    switch (this.masterUser?.gender) {
      case "male":
        return "Herr";
      case "female":
        return "Frau";
    }
  }
}
